import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, Space, Tag, Spin, message, Popconfirm, Divider, Popover, Modal } from 'antd';
import { SendOutlined, UserOutlined, CheckOutlined, CloseOutlined, SafetyCertificateOutlined, ClearOutlined, WechatOutlined, PhoneOutlined, AudioOutlined } from '@ant-design/icons';
import { apiFetch } from '../utils/api.js';
import tinaAvatar from '../assets/tina-avatar.jpg';
import ReportDownload from '../components/ReportDownload.jsx';

const SUGGESTIONS = [
  '今天入库了哪些产品',
  '帮我看下现在有哪些商品是负库存',
  '有哪些采购单还没入库完',
  '检查一下最近有没有编码填错的订单',
  '有哪些售后单退回还没入库',
  'HB-11003 的库存怎么对不上？帮我查查',
];

// 简单 markdown 渲染：**加粗**、#标题、- 列表
function renderText(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  lines.forEach((line, i) => {
    const t = line.trim();
    if (!t) return;
    if (t.startsWith('### ')) out.push(<div key={i} style={{ fontWeight: 500, fontSize: 14, margin: '8px 0 3px', color: 'var(--brand-strong)' }}>{t.slice(4)}</div>);
    else if (t.startsWith('## ')) out.push(<div key={i} style={{ fontWeight: 500, fontSize: 15, margin: '8px 0 3px', color: 'var(--brand-strong)' }}>{t.slice(3)}</div>);
    else if (t.startsWith('# ')) out.push(<div key={i} style={{ fontWeight: 500, fontSize: 16, margin: '8px 0 3px', color: 'var(--brand-strong)' }}>{t.slice(2)}</div>);
    else if (t.startsWith('- ') || t.startsWith('* ')) out.push(<div key={i} style={{ paddingLeft: 14, position: 'relative', margin: '2px 0' }}><span style={{ position: 'absolute', left: 0 }}>•</span>{inline(t.slice(2))}</div>);
    else out.push(<div key={i} style={{ margin: '2px 0' }}>{inline(t)}</div>);
  });
  return out;
}
function inline(t) {
  const parts = String(t).split(/\*\*(.+?)\*\*/g);
  return parts.map((p, i) => i % 2 === 1 ? <b key={i}>{p}</b> : p);
}

// 提案参数友好文案
function proposalParamsText(p) {
  const params = p.params || {};
  switch (p.action) {
    case 'adjust_inventory':
      return params.mode === 'set'
        ? `把库存「${params.spec_code}」设为 ${params.quantity}`
        : `把库存「${params.spec_code}」${Number(params.quantity) >= 0 ? '＋' : ''}${params.quantity}`;
    case 'relink_order':
      return `把订单「${params.platform_order_no}」的编码改为「${params.new_spec_code}」`;
    case 'change_shipping_date':
      return `把订单「${params.platform_order_no}」的发货日期改为 ${params.shipping_date}`;
    default:
      return '';
  }
}

// 缇娜圆形头像（统一复用）
function TinaAvatar({ size = 34, style }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'inline-flex', overflow: 'hidden', border: '1px solid #F4C0D1',
      ...style,
    }}>
      <img src={tinaAvatar} alt="缇娜" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </span>
  );
}

// ===== 语音通话辅助：录音 blob → 16kHz 单声道 WAV（浏览器原生，无需 ffmpeg） =====
const sleep = (ms) => new Promise(r => setTimeout(r, ms));
function writeStr(view, off, str) { for (let i = 0; i < str.length; i++) view.setUint8(off + i, str.charCodeAt(i)); }
function audioBufferToWav(buffer) {
  const numCh = 1, sampleRate = buffer.sampleRate, numFrames = buffer.length;
  const bytesPerSample = 2, blockAlign = numCh * bytesPerSample, byteRate = sampleRate * blockAlign;
  const dataSize = numFrames * blockAlign;
  const ab = new ArrayBuffer(44 + dataSize);
  const v = new DataView(ab);
  writeStr(v, 0, 'RIFF'); v.setUint32(4, 36 + dataSize, true); writeStr(v, 8, 'WAVE');
  writeStr(v, 12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, numCh, true);
  v.setUint32(24, sampleRate, true); v.setUint32(28, byteRate, true); v.setUint16(32, blockAlign, true); v.setUint16(34, 16, true);
  writeStr(v, 36, 'data'); v.setUint32(40, dataSize, true);
  const data = buffer.getChannelData(0);
  let off = 44;
  for (let i = 0; i < numFrames; i++) {
    const s = Math.max(-1, Math.min(1, data[i]));
    v.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    off += 2;
  }
  return ab;
}
function blobToWav16k(blob) {
  return new Promise(async (resolve, reject) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      const tmp = new AC();
      const buf = await blob.arrayBuffer();
      const audioBuffer = await tmp.decodeAudioData(buf);
      tmp.close().catch(() => {});
      const targetRate = 16000;
      const offline = new OfflineAudioContext(1, Math.max(1, Math.ceil(audioBuffer.duration * targetRate)), targetRate);
      const src = offline.createBufferSource();
      src.buffer = audioBuffer; src.connect(offline.destination); src.start(0);
      const rendered = await offline.startRendering();
      resolve(new Blob([audioBufferToWav(rendered)], { type: 'audio/wav' }));
    } catch (e) { reject(e); }
  });
}

export default function TinaAssistant() {
  const [messages, setMessages] = useState([]);
  const [pending, setPending] = useState([]);
  const [pushLog, setPushLog] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const boxRef = useRef(null);

  // ===== 语音通话状态 =====
  const [callOpen, setCallOpen] = useState(false);
  const [callStatus, setCallStatus] = useState('idle'); // idle | listening | thinking | speaking
  const [callText, setCallText] = useState('');
  const [callReply, setCallReply] = useState('');
  const [callError, setCallError] = useState('');
  const callRef = useRef({
    active: false, stream: null, audioCtx: null, analyser: null,
    recorder: null, chunks: [], source: null, rafId: null,
    speaking: false, silenceStart: null, hasSpoken: false,
  });
  const playerRef = useRef(null);

  // 加载缇娜长期记忆（对话历史 + 待确认提案列表）
  useEffect(() => {
    apiFetch('/api/tina/memory').then(r => r.json()).then(d => {
      if (d.data) {
        setMessages(d.data.conversations || []);
        setPending(d.data.pending || []);
      }
    }).catch(() => {});
    // 缇娜发微信日志
    apiFetch('/api/tina/push-log').then(r => r.json()).then(d => {
      if (d.success) setPushLog(d.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [messages, pending, loading]);

  const send = async (text) => {
    const q = (text || input).trim();
    if (!q || loading) return;
    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setInput(''); setLoading(true);
    try {
      const res = await apiFetch('/api/tina/chat', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: q }),
      }).then(r => r.json());
      if (res.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: res.reply }]);
        if (res.pending) setPending(prev => [...prev, res.pending]);
        // 缇娜可能在对话中主动发了微信，刷新推送日志
        apiFetch('/api/tina/push-log').then(r => r.json()).then(d => { if (d.success) setPushLog(d.data || []); }).catch(() => {});
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ ' + (res.error || '缇娜服务异常，请稍后再试') }]);
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: '⚠️ 网络错误，请稍后再试' }]);
    }
    setLoading(false);
  };

  const confirm = async (p) => {
    if (!p) return;
    setExecuting(true);
    try {
      const res = await apiFetch('/api/tina/execute', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposalId: p.id }),
      }).then(r => r.json());
      if (res.success) {
        setMessages(prev => [...prev, { role: 'assistant', content: '✅ ' + res.message }]);
        setPending(prev => prev.filter(x => x.id !== p.id));
        message.success('已执行');
      } else {
        message.error(res.error || '执行失败');
      }
    } catch (e) { message.error('执行失败'); }
    setExecuting(false);
  };

  const reject = async (p) => {
    if (!p) return;
    try {
      await apiFetch('/api/tina/reject', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ proposalId: p.id }),
      }).then(r => r.json());
      setMessages(prev => [...prev, { role: 'assistant', content: '好的，已取消这个方案，不做任何改动。' }]);
      setPending(prev => prev.filter(x => x.id !== p.id));
    } catch (e) { message.error('操作失败'); }
  };

  const clearMemory = async () => {
    try {
      await apiFetch('/api/tina/clear', { method: 'POST' }).then(r => r.json());
      setMessages([]); setPending([]);
      message.success('缇娜的记忆已清空');
    } catch (e) { message.error('清空失败'); }
  };

  // ===== 语音通话逻辑 =====
  const startListening = async () => {
    const r = callRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      r.stream = stream;
      const AC = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AC();
      if (audioCtx.state === 'suspended') await audioCtx.resume();
      r.audioCtx = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      r.source = source;
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.3;
      source.connect(analyser);
      r.analyser = analyser;

      let mime = '';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) mime = 'audio/webm;codecs=opus';
      else if (MediaRecorder.isTypeSupported('audio/webm')) mime = 'audio/webm';
      else if (MediaRecorder.isTypeSupported('audio/mp4')) mime = 'audio/mp4';
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      r.recorder = recorder;
      r.chunks = [];
      recorder.ondataavailable = e => { if (e.data && e.data.size) r.chunks.push(e.data); };
      recorder.onstop = () => { onRecordStop(); };
      recorder.start(200);

      r.speaking = false; r.silenceStart = null; r.hasSpoken = false;
      setCallStatus('listening');
      vadLoop();
    } catch (e) {
      setCallError('无法使用麦克风：' + ((e && e.message) || '请检查浏览器麦克风权限'));
      setCallStatus('idle');
    }
  };

  const vadLoop = () => {
    const r = callRef.current;
    if (!r.active) return;
    const analyser = r.analyser;
    if (!analyser) return;
    const data = new Uint8Array(analyser.fftSize);
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
    const rms = Math.sqrt(sum / data.length);
    const now = Date.now();
    if (rms > 0.03) { r.speaking = true; r.hasSpoken = true; r.silenceStart = null; }
    else if (r.hasSpoken) {
      if (r.silenceStart == null) r.silenceStart = now;
      else if (now - r.silenceStart > 1200) { stopRecording(); return; }
    }
    r.rafId = requestAnimationFrame(vadLoop);
  };

  const stopRecording = () => {
    const r = callRef.current;
    if (r.rafId) { cancelAnimationFrame(r.rafId); r.rafId = null; }
    if (r.recorder && r.recorder.state !== 'inactive') { try { r.recorder.stop(); } catch (e) {} }
    if (r.source) { try { r.source.disconnect(); } catch (e) {} }
    if (r.audioCtx) { r.audioCtx.close().catch(() => {}); r.audioCtx = null; }
    if (r.stream) { r.stream.getTracks().forEach(t => t.stop()); r.stream = null; }
    r.analyser = null;
    setCallStatus('thinking');
  };

  const onRecordStop = async () => {
    const r = callRef.current;
    const chunks = r.chunks; r.chunks = [];
    if (!chunks.length) { if (r.active) startListening(); else setCallStatus('idle'); return; }
    const blob = new Blob(chunks, { type: (chunks[0] && chunks[0].type) || 'audio/webm' });
    if (blob.size < 2000) { if (r.active) startListening(); return; }
    try {
      const wavBlob = await blobToWav16k(blob);
      const fd = new FormData();
      fd.append('audio', wavBlob, 'voice.wav');
      const up = await apiFetch('/api/tina/voice/upload', { method: 'POST', body: fd }).then(x => x.json());
      if (!up.success) throw new Error(up.error || '音频上传失败');
      const talk = await apiFetch('/api/tina/voice/talk', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ audioUrl: up.url }),
      }).then(x => x.json());
      if (!talk.success) throw new Error(talk.error || '缇娜没听清');
      setCallText(talk.recognized || '');
      setCallReply(talk.reply || '');
      setCallError('');
      if (talk.pending) setPending(prev => [...prev, talk.pending]);
      setMessages(prev => [...prev,
        { role: 'user', content: '🎤 ' + (talk.recognized || '') },
        { role: 'assistant', content: talk.reply || '' },
      ]);
      if (talk.audio) {
        setCallStatus('speaking');
        await playAudio(talk.audio);
      }
      if (r.active) startListening();
    } catch (e) {
      setCallError((e && e.message) || '通话出错，请重试');
      if (r.active) { await sleep(1000); startListening(); }
    }
  };

  const playAudio = (url) => new Promise((resolve) => {
    const a = new Audio(url);
    playerRef.current = a;
    a.onended = () => { playerRef.current = null; resolve(); };
    a.onerror = () => { playerRef.current = null; resolve(); };
    a.play().catch(() => { playerRef.current = null; resolve(); });
  });

  const startCall = async () => {
    setCallError(''); setCallText(''); setCallReply('');
    setCallOpen(true);
    callRef.current.active = true;
    await startListening();
  };

  const hangUp = () => {
    const r = callRef.current;
    r.active = false;
    if (r.rafId) { cancelAnimationFrame(r.rafId); r.rafId = null; }
    if (r.recorder && r.recorder.state !== 'inactive') { try { r.recorder.stop(); } catch (e) {} }
    if (r.source) { try { r.source.disconnect(); } catch (e) {} }
    if (r.audioCtx) { r.audioCtx.close().catch(() => {}); r.audioCtx = null; }
    if (r.stream) { r.stream.getTracks().forEach(t => t.stop()); r.stream = null; }
    if (playerRef.current) { try { playerRef.current.pause(); } catch (e) {} playerRef.current = null; }
    setCallOpen(false);
    setCallStatus('idle');
  };

  // 点击麦克风：正在听→立即结束说话并发送；正在说→打断播放、开始听
  const onMicClick = () => {
    if (callStatus === 'listening') stopRecording();
    else if (callStatus === 'speaking') {
      if (playerRef.current) { try { playerRef.current.pause(); } catch (e) {} playerRef.current = null; }
      startListening();
    }
  };

  return (
    <div className="page-container">
      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <TinaAvatar size={34} />
            <span>
              <span style={{ fontSize: 16, fontWeight: 500, color: '#26201C' }}>缇娜</span>
              <span style={{ fontSize: 12, color: '#8c8c8c', marginLeft: 8 }}>仓储 AI 员工 · 订单 / 售后 / 供应链</span>
            </span>
          </span>
        }
        extra={
          <Space>
            <Tag color="magenta" style={{ marginRight: 0 }}>改数字前会先向你汇报</Tag>
            <Popover
              trigger="click"
              title="缇娜最近发的微信"
              content={
                <div style={{ width: 300, maxHeight: 320, overflowY: 'auto' }}>
                  {pushLog.length === 0 ? (
                    <div style={{ color: '#bfbfbf', fontSize: 12, padding: '8px 0' }}>还没有发过微信记录</div>
                  ) : pushLog.slice(0, 20).map(l => (
                    <div key={l.id} style={{ padding: '7px 0', borderBottom: '0.5px solid var(--brand-border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {l.ok
                          ? <Tag color="green" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>已发</Tag>
                          : <Tag color="red" style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}>失败</Tag>}
                        <span style={{ fontSize: 12.5, color: '#26201C', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.title}</span>
                      </div>
                      <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>{l.ts} · {l.source}</div>
                      {!l.ok && l.error && <div style={{ fontSize: 11, color: 'var(--brand)', marginTop: 2 }}>{l.error}</div>}
                    </div>
                  ))}
                </div>
              }
            >
              <Button size="small" type="text" icon={<WechatOutlined />} style={{ color: '#07C160' }} />
            </Popover>
            <Popconfirm title="清空缇娜的全部记忆（对话+经验）？" onConfirm={clearMemory} okText="清空" cancelText="取消">
              <Button size="small" type="text" icon={<ClearOutlined />} style={{ color: '#8c8c8c' }} />
            </Popconfirm>
          </Space>
        }
        style={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 12, overflow: 'hidden' }}
      >
        <div ref={boxRef} style={{ flex: 1, overflowY: 'auto', padding: '4px 8px' }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#8c8c8c' }}>
              <div style={{ textAlign: 'center', marginBottom: 14 }}>
                <TinaAvatar size={56} />
              </div>
              <div style={{ fontSize: 15, color: '#5F5E5A', marginBottom: 6 }}>你好呀，我是缇娜～</div>
              <div style={{ fontSize: 13, marginBottom: 18 }}>仓库里有什么不对的，交给我来查、来想办法。</div>
              <Space wrap size={8}>
                {SUGGESTIONS.map(s => (
                  <Button key={s} size="small" onClick={() => send(s)} style={{ borderRadius: 14, borderColor: 'var(--brand-border)', color: 'var(--brand-strong)' }}>{s}</Button>
                ))}
              </Space>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', marginBottom: 14, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ maxWidth: '82%', display: 'flex', gap: 8, flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
                {m.role === 'user' ? (
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--brand)', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 14,
                  }}>
                    <UserOutlined />
                  </div>
                ) : (
                  <TinaAvatar size={30} />
                )}
                <div style={{
                  background: m.role === 'user' ? '#D85A30' : '#fff', color: m.role === 'user' ? '#fff' : '#333',
                  borderRadius: 12, padding: '10px 14px', fontSize: 13.5, lineHeight: 1.7,
                  boxShadow: m.role === 'user' ? 'none' : '0 1px 4px rgba(0,0,0,0.06)', border: m.role === 'user' ? 'none' : '0.5px solid var(--brand-border)',
                }}>
                  {renderText(m.content)}
                  {m.role === 'assistant' && <ReportDownload text={m.content} color="var(--brand)" />}
                </div>
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: 'flex', gap: 8 }}>
              <TinaAvatar size={30} />
              <div style={{ background: '#fff', borderRadius: 12, padding: '10px 14px', border: '0.5px solid var(--brand-border)' }}><Spin size="small" /> <span style={{ marginLeft: 8, color: '#8c8c8c' }}>缇娜正在查数据、想方案…</span></div>
            </div>
          )}
        </div>

        {/* 提案确认卡片列表（安全闸门） */}
        {pending.length > 0 && (
          <div style={{ marginTop: 8, maxHeight: 240, overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
              <SafetyCertificateOutlined style={{ color: '#BA7517' }} />
              <span style={{ fontWeight: 500, color: '#633806', fontSize: 13 }}>缇娜有 {pending.length} 条方案等你确认</span>
            </div>
            {pending.map(p => (
              <div key={p.id} style={{
                border: '1px solid #EF9F27', background: '#FFFBF2', borderRadius: 12, padding: '12px 14px', marginBottom: 8,
              }}>
                <div style={{ fontSize: 13.5, color: '#5F5E5A', lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 500, color: '#26201C' }}>{proposalParamsText(p)}</div>
                  {p.source === 'audit' && <Tag color="gold" style={{ marginTop: 6, fontSize: 11 }}>来自库存巡检</Tag>}
                  {p.reason && <div style={{ marginTop: 4, fontSize: 12.5, color: '#8c8c8c' }}>原因：{p.reason}</div>}
                </div>
                <Divider style={{ margin: '10px 0' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button type="primary" size="small" icon={<CheckOutlined />} loading={executing} onClick={() => confirm(p)} style={{ background: 'var(--brand)', borderColor: 'var(--brand)' }}>确认执行</Button>
                  <Button size="small" icon={<CloseOutlined />} onClick={() => reject(p)}>驳回</Button>
                  <span style={{ fontSize: 11.5, color: '#bfbfbf', alignSelf: 'center' }}>确认后才会真正改数据</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, paddingTop: 10, borderTop: '0.5px solid var(--brand-border)' }}>
          <Input
            placeholder="跟缇娜说，例如：帮我看下哪些商品负库存了"
            value={input}
            onChange={e => setInput(e.target.value)}
            onPressEnter={() => send()}
            allowClear
          />
          <Button icon={<PhoneOutlined />} onClick={startCall} title="语音通话" style={{ color: '#07C160', borderColor: '#07C160' }} />
          <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={() => send()} style={{ background: 'var(--brand)', borderColor: 'var(--brand)' }}>发送</Button>
        </div>
      </Card>

      {/* ===== 语音通话面板 ===== */}
      <Modal
        open={callOpen}
        footer={null}
        closable={false}
        width={400}
        centered
        onCancel={hangUp}
        bodyStyle={{ padding: 0, background: 'var(--brand-bg)', borderRadius: 16 }}
      >
        <div style={{ padding: '28px 22px 22px', textAlign: 'center' }}>
          <TinaAvatar size={72} />
          <div style={{ fontSize: 17, fontWeight: 600, color: '#26201C', marginTop: 10 }}>缇娜</div>
          <div style={{ fontSize: 12.5, color: '#8c8c8c', marginTop: 3, minHeight: 20 }}>
            {callStatus === 'listening' ? '我在听，请说～' : callStatus === 'thinking' ? '让我查一查、想一想…' : callStatus === 'speaking' ? '我在说，你可以随时打断' : ''}
          </div>

          <div style={{ margin: '22px 0 6px' }}>
            <button
              onClick={onMicClick}
              className={callStatus === 'listening' ? 'tina-mic-pulse' : callStatus === 'speaking' ? 'tina-speaking' : ''}
              style={{
                width: 88, height: 88, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: callStatus === 'listening' ? '#D85A30' : callStatus === 'speaking' ? '#07C160' : '#F3E2D8',
                color: '#fff', fontSize: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all .2s',
              }}
            >
              {callStatus === 'speaking' ? <PhoneOutlined /> : <AudioOutlined />}
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#bfbfbf' }}>
            {callStatus === 'listening' ? '说完会自动识别，也可点一下立即发送' : callStatus === 'speaking' ? '点一下打断我' : ''}
          </div>

          {(callText || callReply) && (
            <div style={{ textAlign: 'left', background: '#fff', borderRadius: 12, padding: '12px 14px', marginTop: 16, maxHeight: 150, overflowY: 'auto', border: '0.5px solid var(--brand-border)' }}>
              {callText && <div style={{ fontSize: 12.5, color: 'var(--brand)', marginBottom: 6 }}>你说：{callText}</div>}
              {callReply && <div style={{ fontSize: 12.5, color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{callReply}</div>}
            </div>
          )}

          {callError && <div style={{ color: 'var(--brand)', fontSize: 12.5, marginTop: 10 }}>{callError}</div>}

          <div style={{ marginTop: 22 }}>
            <button
              onClick={hangUp}
              style={{
                width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
                background: '#E5484D', color: '#fff', fontSize: 20,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <PhoneOutlined style={{ transform: 'rotate(135deg)' }} />
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#bfbfbf', marginTop: 6 }}>挂断</div>
        </div>
      </Modal>
    </div>
  );
}
